import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Drawer,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Pagination,
  Popconfirm,
  Popover,
  Result,
  Table,
  Tabs,
  Tag,
  Upload,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Router from "../..";
import { useUserStore } from "../../../stores/userStore";
import {
  useAppliedInfluencersByBrand,
  useCampaignListByBrand,
  useDeleteCampaign,
  useInvitedInfluencersByBrand,
  useManageCampaignRequest,
  useSendFeedback,
  useUpdateCampaign,
  useUpdateTrackingNumber,
  useUploadShipmentExcel,
} from "../../hooks/useCampaign";
import { BRAND_STATUS } from "../../utils/campaignStatus";
import { parseContentFormatValues } from "../../utils/contentFormat";
import { fileToBase64 } from "../../utils/fileToBase64";
import { parseMissionPayload } from "../../utils/mission";
import { getCourierCode } from "../../utils/shipment/courierUtils";
import { createShipmentExcel } from "../../utils/shipment/shipmentExcelDownload";
import { parseExcelToJson } from "../../utils/shipment/shipmentExcelUpload";
import { createSettlementExcel } from "../../utils/settlementExcelDownload";

export const BrandCampaignManagement = () => {
  const { userInfo } = useUserStore();
  const history = useHistory();
  const [isDetailDrawerVisible, setIsDetailDrawerVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  // 파일 상단에 새로운 state 추가
  const [form] = Form.useForm();
  const [feedbackForm] = Form.useForm();

  const [editingCampaign, setEditingCampaign] = useState(null);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAppliedList, setShowAppliedList] = useState(false);
  const [influencerListStatus, setInfluencerListStatus] = useState(null);
  const [isInvitedTab, setIsInvitedTab] = useState(false);
  const pageSize = 20;

  const queryClient = useQueryClient();
  const { data: campaignsData } = useCampaignListByBrand({
    page: currentPage,
    status: currentStatus,
  });
  const isCampaignListError = campaignsData === null;
  const campaigns = Array.isArray(campaignsData?.result)
    ? campaignsData.result
    : [];

  const total = campaignsData?.total ?? 0;

  const displayCampaign =
    selectedCampaignId != null
      ? campaigns.find((c) => c.id === selectedCampaignId)
      : null;

  const shouldFetchInfluencerList = !!selectedCampaignId && showAppliedList;
  const { data: invitedData } = useInvitedInfluencersByBrand({
    campaignId: selectedCampaignId,
    status: influencerListStatus,
    enabled: shouldFetchInfluencerList,
  });
  const { data: appliedData } = useAppliedInfluencersByBrand({
    campaignId: selectedCampaignId,
    status: influencerListStatus,
    enabled: shouldFetchInfluencerList,
  });

  const [shipmentInfo, setShipmentInfo] = useState({});

  const [isTrackingModalVisible, setIsTrackingModalVisible] = useState(false);
  const [selectedCampaignRecruitmentId, setSelectedCampaignRecruitmentId] =
    useState(null);
  const [trackingForm] = Form.useForm();
  const location = useLocation();

  // 원고 제출 보기 모달
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [feedbackModal, setFeedbackModal] = useState({
    open: false,
    record: null,
    type: null,
  });

  const getMissionFormValues = (campaign) =>
    parseMissionPayload(campaign?.mission);

  const handleOpenModal = (record) => {
    setSelectedRecord(record);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRecord(null);
  };

  const { mutateAsync: manageCampaignRequest } = useManageCampaignRequest();
  const { mutateAsync: sendFeedback, isPending: isSendingFeedback } =
    useSendFeedback();

  // 초대/신청 둘 다 갱신할 때만 사용 (드로어에서 목록 열 때 등). 신청만 바뀐 경우엔 applied만 invalidate.
  const invalidateInfluencerLists = () => {
    queryClient.invalidateQueries({
      queryKey: ["campaign", "invited", "brand"],
    });
    queryClient.invalidateQueries({
      queryKey: ["campaign", "applied", "brand"],
    });
  };

  const handleInfluencerAction = async (item, status) => {
    const success = await manageCampaignRequest({
      id: item.id,
      campaign_id: item.campaign_id,
      status,
    });

    if (!success) return;
  };

  const canSendFeedback = (record) =>
    Boolean(!record?.feedback) || Boolean(record?.is_checked_feedback);

  const getDraftFeedbackLabel = (record) =>
    record?.feedback && record?.is_checked_feedback
      ? "추가 피드백"
      : "피드백하기";

  const renderDraftReviewStatus = (record) => {
    if (record?.feedback && record?.is_checked_feedback) {
      return (
        <Tag color="success" className="m-0 whitespace-nowrap">
          피드백 반영
        </Tag>
      );
    }

    if (record?.feedback) {
      return (
        <Tag color="gold" className="m-0 whitespace-nowrap">
          피드백 확인 대기
        </Tag>
      );
    }
  };

  const renderDraftReviewActions = (record) => {
    if (!record?.confirm_url) return null;

    return (
      <div className="flex flex-col items-center gap-1">
        <Popconfirm
          title="원고 승인"
          description="원고 승인 후에는 더 이상 수정할 수 없습니다. 계속하시겠습니까?"
          onConfirm={() => handleInfluencerAction(record, "confirmed")}
          okText="확인"
          cancelText="취소"
          placement="left"
        >
          <Button
            type="primary"
            size="small"
            onClick={(e) => e.stopPropagation()}
            className="text-[12px] px-2"
          >
            원고 승인하기
          </Button>
        </Popconfirm>
        {canSendFeedback(record) && (
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenFeedbackModal(record, "draft");
            }}
            className="text-[12px] px-2"
          >
            {getDraftFeedbackLabel(record)}
          </Button>
        )}
      </div>
    );
  };

  const handleOpenFeedbackModal = (record, type) => {
    feedbackForm.resetFields();
    setFeedbackModal({ open: true, record, type });
  };

  const handleCloseFeedbackModal = () => {
    setFeedbackModal({ open: false, record: null, type: null });
    feedbackForm.resetFields();
  };

  const handleSendFeedback = async ({ feedback }) => {
    const record = feedbackModal.record;
    const trimmedFeedback = feedback?.trim();

    if (!trimmedFeedback) {
      message.warning("피드백 내용을 입력해 주세요.");
      return;
    }

    if (!record?.campaign_id || !record?.user_id) {
      message.error("피드백을 보낼 크리에이터 정보를 찾을 수 없습니다.");
      return;
    }

    const success = await sendFeedback({
      campaign_id: record.campaign_id,
      user_id: record.user_id,
      feedback: trimmedFeedback,
    });

    if (success) {
      handleCloseFeedbackModal();
    }
  };

  const handleOpenInfluencerSearch = async (record) => {
    const influencerId = record?.tiktok_influencer_id;

    if (!influencerId) {
      message.warning("크리에이터 정보를 찾을 수 없습니다.");
      return;
    }

    const influencerDetail =
      await Router.Api.getInfluencerDetailById(influencerId);
    const uniqueId = influencerDetail?.influencer_info?.unique_id;

    if (!uniqueId) {
      message.warning("크리에이터 상세 정보를 찾을 수 없습니다.");
      return;
    }

    history.push(`/influencer/search?influencer_id=${uniqueId}`);
  };

  const handleSettlement = (campaign, settlementMode, selectedId) => {
    history.push(`/campaign/settlement?campaign_id=${campaign.id}`, {
      campaign,
      settlementMode,
      selectedId,
    });
  };

  const handleDownloadSettlementExcel = async () => {
    const data = await Router.Api.getBrandPaidList(displayCampaign.id);
    if (!data || data.length === 0) {
      message.warning("다운로드할 정산 내역이 없습니다.");
      return;
    }

    await createSettlementExcel(data, `${displayCampaign.title}_정산내역.xlsx`);
    message.success("엑셀 파일이 다운로드되었습니다.");
  };

  // 배송지 정보 확인하기
  const handleCheckShipmentInfo = async (campaign_recruitment_id) => {
    const data = await Router.Api.getInfluencerShipmentInfo(
      campaign_recruitment_id
    );
    if (data?.result) {
      setShipmentInfo((prev) => ({
        ...prev,
        [campaign_recruitment_id]: data.result,
      }));
    }
  };

  // 운송장번호 등록 모달 열기
  const handleOpenTrackingModal = (record) => {
    if (userInfo.type_info.grade === "viewer") {
      message.warning("viewer 권한은 조회만 가능합니다.");
      return;
    }
    setSelectedCampaignRecruitmentId(record.id);
    trackingForm.resetFields();
    setIsTrackingModalVisible(true);
  };

  // 운송장번호 등록
  const handleSubmitTracking = async (values) => {
    let shipment = shipmentInfo[selectedCampaignRecruitmentId];
    if (!shipment) {
      const shipmentResponse = await Router.Api.getInfluencerShipmentInfo(
        selectedCampaignRecruitmentId
      );
      shipment = shipmentResponse?.result;
    }

    const shipmentData = {
      id: shipment?.id,
      courier_company: values.courier_company,
      courier_code: getCourierCode(values.courier_company),
      tracking_number: values.tracking_number,
      sender_name: values.sender_name,
      sender_phone: values.sender_phone,
      products: values.products || [],
    };

    const success = await updateTrackingNumber(shipmentData);

    if (success) {
      setIsTrackingModalVisible(false);
      trackingForm.resetFields();

      // 해당 크리에이터의 배송 정보만 업데이트
      await handleCheckShipmentInfo(selectedCampaignRecruitmentId);

      setSelectedCampaignRecruitmentId(null);
    }
  };

  const handleCampaignClick = async (campaign, shouldUpdateUrl = true) => {
    if (shouldUpdateUrl) {
      history.replace(`/campaign/manage?campaign_id=${campaign.id}`);
    }

    setSelectedCampaignId(campaign.id);
    setIsDetailDrawerVisible(true);
  };

  // URL 파라미터 처리
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const campaignId = urlParams.get("campaign_id");

    if (campaignId && campaigns.length > 0 && !isDetailDrawerVisible) {
      const campaign = campaigns.find(
        (c) => String(c.id) === String(campaignId)
      );
      if (campaign) {
        handleCampaignClick(campaign, false);
      }
    }
  }, [location.search, campaigns]);

  const { mutateAsync: updateCampaign } = useUpdateCampaign();
  const { mutateAsync: updateTrackingNumber } = useUpdateTrackingNumber();
  const { mutateAsync: uploadShipmentExcel } = useUploadShipmentExcel();

  const handleUpdate = async (values) => {
    let thumbnailBuffer = null;
    let attachmentBuffer = null;

    if (thumbnailFile) {
      thumbnailBuffer = await fileToBase64(thumbnailFile);
    }
    if (attachmentFile) {
      attachmentBuffer = await fileToBase64(attachmentFile);
    }

    const contentFormat = parseContentFormatValues(values.content_format).join(
      ","
    );

    const isPaid = values.seeding_type === "paid";
    const campaignData = {
      manager_user_id: values.manager_user_id,
      title: values.title,
      description: values.description,
      mission: values.mission,
      content_format: contentFormat,
      campaign_category: values.category,
      seeding_type: values.seeding_type,
      recruit_count: values.recruitment_count,
      budget: "0.00",
      payout:
        isPaid && values.payout != null
          ? values.payout.toString() + ".00"
          : "0.00",
      currency: isPaid && values.currency ? values.currency : "KRW",
      start_at: values.start_date.format("YYYY-MM-DD") + "T00:00:00Z",
      end_at: values.end_date.format("YYYY-MM-DD") + "T14:59:59Z",
      requires_shipment: values.requires_shipment,
      product_info: values.product_info,
      allow_secondary_use: values.allow_secondary_use,
    };
    // 새로운 썸네일이 있을 때만 버퍼 변환값을 보내고, 없으면 기존 값 사용
    if (thumbnailFile) {
      // 새 파일이 있으면 buffer 사용
      if (thumbnailBuffer) {
        campaignData.thumbnail = thumbnailBuffer;
      }
    } else if (editingCampaign?.thumbnail) {
      // 새 파일이 없고 기존 값이 있으면 기존 값 유지
      campaignData.thumbnail = editingCampaign.thumbnail;
    }

    // 상세 이미지 처리
    if (attachmentFile) {
      if (attachmentBuffer) {
        campaignData.attachment_url = attachmentBuffer;
      }
    } else if (editingCampaign?.attachment_url) {
      campaignData.attachment_url = editingCampaign.attachment_url;
    }

    const success = await updateCampaign({
      id: editingCampaign.id,
      ...campaignData,
    });

    if (success) {
      setIsEditMode(false);
      setEditingCampaign(null);
      form.resetFields();
      setThumbnailFile(null);
      setAttachmentFile(null);
    }
  };

  const { mutateAsync: deleteCampaign } = useDeleteCampaign();

  const handleDelete = async (campaignId) => {
    Modal.confirm({
      title: "캠페인 삭제",
      content: "정말로 이 캠페인을 삭제하시겠습니까?",
      okText: "삭제",
      cancelText: "취소",
      okType: "danger",
      async onOk() {
        const success = await deleteCampaign({ campaignId });
        if (success) {
          setIsDetailDrawerVisible(false);
          setSelectedCampaignId(null);
          history.replace("/campaign/manage");
        }
      },
    });
  };

  const handleCreateCampaignClick = () => {
    const { canCreate, reason } = canCreateCampaigns();

    if (!canCreate) {
      switch (reason) {
        case "viewer":
          Modal.warning({
            title: "권한 없음",
            content: "viewer 등급은 권한이 없습니다. 관리자에게 문의하세요.",
            okText: "확인",
          });
          return;
        case "registration":
          Modal.confirm({
            title: "사업자 등록증이 필요합니다.",
            content: "캠페인을 등록하려면 먼저 사업자 등록증을 업로드해주세요.",
            okText: "워크스페이스로 이동하기",
            cancelText: "취소",
            onOk: () => {
              history.push("/workspace/info");
            },
          });
          return;
        case "bank_and_approval":
          Modal.warning({
            title: "브랜드 정보를 확인하세요.",
            content:
              "통장 사본을 등록하고 관리자 승인을 받아야만 캠페인을 등록할 수 있습니다.",
            okText: "확인",
          });
          return;
        default:
          Modal.error({
            title: "오류",
            content: "캠페인을 등록할 수 없습니다.",
            okText: "확인",
          });
          return;
      }
    }

    // 새 페이지로 이동
    history.push("/campaign/create");
  };

  const canManageCampaigns =
    userInfo?.type_info && userInfo?.type_info?.grade !== "viewer";

  // 캠페인 생성 가능 여부 체크 함수
  const canCreateCampaigns = () => {
    // userInfo가 아직 로드되지 않았거나 type_info가 없으면 체크 불가
    if (!userInfo || !userInfo.type_info) {
      return { canCreate: false, reason: "default" };
    }
    if (userInfo.type_info.grade === "viewer") {
      return { canCreate: false, reason: "viewer" };
    }

    const hasRegistration = userInfo.type_info.registration_img !== "";
    const hasBankAccount = userInfo.type_info.bank_account_url !== null;
    const isApproved = userInfo.type_info.is_bank_verified;
    if (!hasRegistration) {
      return { canCreate: false, reason: "registration" };
    }
    if (!hasBankAccount || !isApproved) {
      return { canCreate: false, reason: "bank_and_approval" };
    }
    return { canCreate: true, reason: null };
  };

  // type_info가 없으면 브랜드 가입 안내 표시
  if (!userInfo?.type_info) {
    return (
      <div>
        <Result
          title="브랜드 가입 또는 생성이 필요합니다"
          subTitle="캠페인을 관리하려면 먼저 브랜드에 가입하거나 생성해주세요."
          extra={
            <Button
              type="primary"
              key="console"
              onClick={() => history.push("/workspace/info")}
            >
              워크스페이스로 이동하기
            </Button>
          }
        />
      </div>
    );
  }

  // 배송 엑셀 파일 다운로드
  const handleDownloadExcel = async () => {
    const data = await Router.Api.getUnshippedAddress(displayCampaign.id);

    if (!data?.result || data.result.length === 0) {
      message.warning("운송장번호를 입력할 지원자가 없습니다.");
      return;
    }

    await createShipmentExcel({ result: data.result });
    message.success("엑셀 파일이 다운로드되었습니다.");
  };

  const handleUploadExcel = (file) => {
    const fileName = file.name;

    Modal.confirm({
      title: "엑셀 파일 업로드 확인",
      content: `"${fileName}" 파일을 업로드하시겠습니까?`,
      okText: "확인",
      cancelText: "취소",
      onOk: async () => {
        const parsedData = await parseExcelToJson(file);

        if (!parsedData || parsedData.length === 0) {
          message.warning("엑셀 파일에 데이터가 없습니다.");
          return;
        }

        await uploadShipmentExcel(parsedData);
      },
    });

    return false;
  };

  console.log("displayCampaign", displayCampaign);
  console.log("rv", userInfo?.type_info);

  // selectedCampaign={displayCampaign}
  //                 brandRecentVideos={userInfo?.type_info?.recent_tiktok_videos}

  return (
    <div>
      <h2 className="md:text-4xl text-3xl font-bold">캠페인 관리</h2>
      <div className="mb-5">
        <Button
          type="primary"
          size="small"
          className="text-lg !bg-[#171717] md:!h-[32px] md:!px-[15px] md:!py-[4px] md:!text-2xl"
          onClick={handleCreateCampaignClick}
        >
          + 신규 캠페인 추가
        </Button>
      </div>
      <Tabs
        defaultActiveKey="all"
        onChange={(key) => {
          const statusMap = {
            0: "pending",
            1: "active",
            2: "finished",
            3: "rejected",
          };
          setCurrentStatus(key === "all" ? null : statusMap[key]);
          setCurrentPage(1);
        }}
        className="font-normal mb-5"
        items={[
          { key: "all", label: "전체" },
          { key: "0", label: "승인 대기 중" },
          { key: "1", label: "진행 중" },
          { key: "2", label: "종료" },
          { key: "3", label: "반려" },
        ]}
      />
      {isCampaignListError ? (
        <div className="py-12">
          <Empty
            description="캠페인 목록을 불러오지 못했습니다. 새로고침 후 다시 시도해주세요."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button size="small" onClick={() => window.location.reload()}>
              새로고침
            </Button>
          </Empty>
        </div>
      ) : (
        <div className="grid grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5 gap-[15px] md:gap-[25px]">
          {campaigns.map((campaign) => (
            <Router.CampaignCard
              key={campaign.id}
              campaign={campaign}
              variant="brand-manage"
              selected={selectedCampaignId === campaign.id}
              onClick={async () => {
                // 이전 선택된 캠페인과 다른 캠페인을 선택했을 때만 초기화 작업 수행
                if (selectedCampaignId !== campaign.id) {
                  setIsEditMode(false);
                  setShowAppliedList(false);
                  setIsInvitedTab(false);
                  form.resetFields();
                  handleCampaignClick(campaign, true);
                  invalidateInfluencerLists();
                } else {
                  setIsDetailDrawerVisible(true);
                }
              }}
            />
          ))}
        </div>
      )}
      <Drawer
        title={
          isEditMode
            ? "캠페인 수정"
            : !isEditMode && showAppliedList
              ? "신청 크리에이터 목록"
              : "캠페인 상세보기"
        }
        placement="right"
        onClose={(e) => {
          e?.stopPropagation();
          setIsDetailDrawerVisible(false);
          setSelectedCampaignId(null);
          setIsEditMode(false);
          setShowAppliedList(false);
          setIsInvitedTab(false);
          setInfluencerListStatus(null);
          form.resetFields();
          history.replace("/campaign/manage");
        }}
        onClick={(e) => e.stopPropagation()} // 드로워 전체에 이벤트 전파 중단 추가
        open={isDetailDrawerVisible}
        mask={false}
        maskClosable={true}
        width={showAppliedList ? 950 : 600}
        footer={null}
        extra={
          <div className="flex gap-2">
            {isEditMode ? (
              <>
                <Button
                  onClick={() => {
                    setIsEditMode(false);
                    setEditingCampaign(null);
                    form.resetFields();
                    if (!displayCampaign) {
                      setIsDetailDrawerVisible(false);
                    }
                  }}
                >
                  취소
                </Button>
                <Button type="primary" onClick={() => form.submit()}>
                  저장
                </Button>
              </>
            ) : (
              displayCampaign &&
              (displayCampaign?.status === "pending" ? (
                <>
                  {canManageCampaigns && (
                    <Button
                      type="primary"
                      onClick={() => {
                        setIsEditMode(true);
                        setEditingCampaign(displayCampaign);
                        const missionFields =
                          getMissionFormValues(displayCampaign);
                        form.setFieldsValue({
                          title: displayCampaign.title,
                          description: displayCampaign.description,
                          ...missionFields,
                          content_format: parseContentFormatValues(
                            displayCampaign.content_format
                          ),
                          category: displayCampaign.campaign_category,
                          seeding_type: displayCampaign.seeding_type,
                          recruitment_count: displayCampaign.recruit_count,
                          payout: displayCampaign.payout,
                          currency: displayCampaign.currency,
                          requires_shipment:
                            !!displayCampaign.requires_shipment,
                          start_date:
                            displayCampaign.start_at &&
                            dayjs(displayCampaign.start_at),
                          end_date:
                            displayCampaign.end_at &&
                            dayjs(displayCampaign.end_at),
                          product_info: displayCampaign.product_info,
                          allow_secondary_use: Boolean(
                            displayCampaign.allow_secondary_use
                          ),
                          manager_user_id: displayCampaign.manager_user_id,
                        });
                      }}
                    >
                      수정
                    </Button>
                  )}
                  {canManageCampaigns && (
                    <Button
                      type="primary"
                      danger
                      onClick={() => {
                        handleDelete(displayCampaign.id);
                      }}
                    >
                      삭제
                    </Button>
                  )}
                </>
              ) : (
                <>
                  {displayCampaign?.status === "active" &&
                    !showAppliedList &&
                    canManageCampaigns && (
                      <Button
                        type="primary"
                        size="small"
                        className="md:!h-[32px] md:!px-[15px] md:!py-[4px] md:!text-2xl"
                        onClick={() => {
                          setIsEditMode(true);
                          setEditingCampaign(displayCampaign);
                          const missionFields =
                            getMissionFormValues(displayCampaign);
                          form.setFieldsValue({
                            title: displayCampaign.title,
                            description: displayCampaign.description,
                            ...missionFields,
                            content_format: parseContentFormatValues(
                              displayCampaign.content_format
                            ),
                            category: displayCampaign.campaign_category,
                            seeding_type: displayCampaign.seeding_type,
                            recruitment_count: displayCampaign.recruit_count,
                            payout: displayCampaign.payout,
                            currency: displayCampaign.currency,
                            requires_shipment:
                              !!displayCampaign.requires_shipment,
                            start_date:
                              displayCampaign.start_at &&
                              dayjs(displayCampaign.start_at),
                            end_date:
                              displayCampaign.end_at &&
                              dayjs(displayCampaign.end_at),
                            product_info: displayCampaign.product_info,
                            allow_secondary_use: Boolean(
                              displayCampaign.allow_secondary_use
                            ),
                            manager_user_id: displayCampaign.manager_user_id,
                          });
                        }}
                      >
                        수정
                      </Button>
                    )}
                  <Button
                    type="primary"
                    size="small"
                    className="md:!h-[32px] md:!px-[15px] md:!py-[4px] md:!text-2xl"
                    style={{
                      backgroundColor: "#171717",
                    }}
                    onClick={() => {
                      const newShowAppliedList = !showAppliedList;
                      setShowAppliedList(newShowAppliedList);
                      setIsInvitedTab(false); // 초대 탭 초기화
                      setInfluencerListStatus(null); // 상태 필터 초기화
                      if (newShowAppliedList) {
                        // 신청자 목록 갱신
                        invalidateInfluencerLists();
                      }
                    }}
                  >
                    {showAppliedList ? "캠페인 상세 정보" : "신청자 목록"}
                  </Button>
                </>
              ))
            )}
          </div>
        }
      >
        {/* 캠페인 상세보기 */}
        {isDetailDrawerVisible && displayCampaign && !isEditMode ? (
          <div>
            {!showAppliedList ? (
              <>
                <Router.CampaignDetailInfo
                  selectedCampaign={displayCampaign}
                  tiktokVideos={userInfo?.type_info?.recent_tiktok_videos}
                  instagramContents={
                    userInfo?.type_info?.recent_instagram_contents
                  }
                  youtubeVideos={userInfo?.type_info?.recent_youtube_videos}
                />
              </>
            ) : (
              // 신청한 크리에이터 목록
              <>
                <Tabs
                  defaultActiveKey="all"
                  onChange={(key) => {
                    const isInvited = key === "invited";
                    setIsInvitedTab(isInvited);
                    setInfluencerListStatus(key === "all" ? null : key);
                  }}
                  className="mb-5 font-normal"
                  items={[
                    { key: "all", label: "전체" },
                    { key: "invited", label: "초대" },
                    { key: "pending", label: "검토 중" },
                    { key: "accepted", label: "수락" },
                    { key: "completed", label: "완료(정산)" },
                    { key: "rejected", label: "거절" },
                    { key: "cancelled", label: "취소" },
                  ]}
                />
                {!isInvitedTab &&
                  influencerListStatus !== "completed" &&
                  displayCampaign.requires_shipment === 1 && (
                    <>
                      <Alert
                        message="배송정보 관리 안내"
                        description={
                          <div className="text-[13px]">
                            <div className="mb-[4px]">
                              • 운송장 입력 파일을 다운로드하면 운송장번호가
                              입력되지 않은 지원자들의 정보가 자동으로
                              포함됩니다.
                            </div>
                            <div>
                              • 한 배송지에 여러 상품을 보내는 경우, 상품 수만큼
                              해당 배송지의 행을 복사한 뒤, 운송장번호는
                              동일하게 유지하고 상품 정보만 각각 다르게 입력해
                              주세요.
                            </div>
                          </div>
                        }
                        type="info"
                        showIcon
                        className="mb-[16px] py-[8px] px-[12px] text-[12px]"
                      />
                      <div className="mb-4 flex gap-2 justify-end">
                        <Button
                          type="default"
                          icon={<DownloadOutlined />}
                          onClick={handleDownloadExcel}
                        >
                          운송장 입력용 파일 다운
                        </Button>
                        <Upload
                          beforeUpload={handleUploadExcel}
                          showUploadList={false}
                          accept=".xlsx, .xls"
                        >
                          <Button icon={<UploadOutlined />}>
                            운송장 일괄 업로드
                          </Button>
                        </Upload>
                      </div>
                    </>
                  )}
                {!isInvitedTab &&
                  influencerListStatus === "completed" &&
                  displayCampaign.seeding_type === "paid" && (
                    <>
                      <Alert
                        message="결제 취소 후에는 정산이 바로 진행되지 않을 수 있습니다."
                        description={
                          <>
                            결제를 취소하면 결제사(PG)에서 환불이 확정된 후{" "}
                            크리에이터 정산을 다시 진행할 수 있습니다.
                            <p>
                              환불 처리에는 시간이 걸릴 수 있어, 정산하기 버튼이
                              나타나지 않을 수 있습니다.
                            </p>
                          </>
                        }
                        type="info"
                        showIcon
                        className="mb-4 text-[13px] leading-relaxed"
                      />
                      <div className="mb-4 flex gap-4 justify-end">
                        <Button
                          type="primary"
                          onClick={() => {
                            handleSettlement(displayCampaign, "bulk");
                          }}
                        >
                          일괄 정산
                        </Button>
                        <Button
                          type="default"
                          icon={<DownloadOutlined />}
                          onClick={handleDownloadSettlementExcel}
                        >
                          정산 파일 다운
                        </Button>
                      </div>
                    </>
                  )}
                <Table
                  dataSource={
                    isInvitedTab
                      ? (invitedData?.result ?? [])
                      : (appliedData?.result ?? [])
                  }
                  onRow={() => ({
                    className: "cursor-default",
                  })}
                  scroll={{ x: "max-content" }}
                  columns={(isInvitedTab
                    ? [
                        {
                          title: "닉네임",
                          dataIndex: "nickname",
                          key: "nickname",
                          width: 100,
                          align: "center",
                          render: (text, record) => (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenInfluencerSearch(record);
                              }}
                              className="break-all whitespace-pre-wrap text-left text-[#1677ff] underline underline-offset-2 disabled:cursor-not-allowed disabled:text-[#999]"
                              disabled={!record?.tiktok_influencer_id}
                            >
                              {text || "닉네임 없음"}
                            </button>
                          ),
                        },
                        {
                          title: "이메일",
                          dataIndex: "email",
                          key: "email",
                          width: 150,
                          align: "center",
                          render: (text) => (
                            <div className="break-all whitespace-pre-wrap">
                              {text || "-"}
                            </div>
                          ),
                        },
                        {
                          title: "소셜 플랫폼",
                          dataIndex: "social",
                          key: "social",
                          width: 100,
                          align: "center",
                          render: (social) => (
                            <div className="break-all whitespace-pre-wrap">
                              {social}
                            </div>
                          ),
                        },
                        {
                          title: "초대일",
                          dataIndex: "created_at",
                          key: "created_at",
                          width: 120,
                          align: "center",
                          render: (date) =>
                            date ? dayjs(date).format("YYYY-MM-DD HH:mm") : "-",
                        },
                        {
                          title: "초대 메시지",
                          key: "message",
                          width: 100,
                          align: "center",
                          render: (_, record) =>
                            record.message ? (
                              <Popover
                                content={
                                  <div className="max-w-[300px] whitespace-pre-wrap">
                                    {record.message}
                                  </div>
                                }
                                title="초대 메시지"
                                trigger="click"
                              >
                                <Button size="small">보기</Button>
                              </Popover>
                            ) : (
                              "없음"
                            ),
                        },
                        {
                          title: "초대 상태",
                          dataIndex: "invitation_status",
                          key: "invitation_status",
                          width: 100,
                          align: "center",
                          render: (status) => {
                            const statusMap = {
                              invited: { text: "대기 중", color: "#1976d2" },
                              accepted: { text: "수락", color: "#2e7d32" },
                              rejected: { text: "거절", color: "#c62828" },
                            };
                            const statusInfo = statusMap[status];
                            return (
                              <Tag
                                style={{
                                  backgroundColor: `${statusInfo.color}20`,
                                  color: statusInfo.color,
                                  border: "none",
                                  borderRadius: "8px",
                                  margin: 0,
                                }}
                              >
                                {statusInfo.text}
                              </Tag>
                            );
                          },
                        },
                      ]
                    : [
                        {
                          title: "닉네임",
                          dataIndex: "nickname",
                          key: "nickname",
                          width: 100,
                          align: "center",
                          render: (text, record) => (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenInfluencerSearch(record);
                              }}
                              className="break-all whitespace-pre-wrap text-left text-[#1677ff] underline underline-offset-2 disabled:cursor-not-allowed disabled:text-[#999]"
                              disabled={!record?.tiktok_influencer_id}
                            >
                              {text || "닉네임 없음"}
                            </button>
                          ),
                        },
                        {
                          title: "성별",
                          dataIndex: "gender",
                          key: "gender",
                          width: 70,
                          align: "center",
                          render: (gender) =>
                            ({
                              male: "남성",
                              female: "여성",
                            })[gender] || "기타",
                        },
                        {
                          title: "신청일",
                          dataIndex: "created_at",
                          key: "created_at",
                          width: 100,
                          align: "center",
                          render: (date) =>
                            dayjs(date).format("YYYY-MM-DD HH:mm"),
                        },
                        {
                          title: "첨부파일",
                          dataIndex: "attachment_url",
                          key: "attachment_url",
                          width: 90,
                          align: "center",
                          render: (url) =>
                            url ? (
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-center"
                              >
                                파일
                              </a>
                            ) : (
                              "-"
                            ),
                        },
                        {
                          title: "메시지",
                          key: "message",
                          width: 70,
                          align: "center",
                          render: (_, record) =>
                            record.message ? (
                              <Popover
                                content={
                                  <div className="max-w-[200px] whitespace-pre-wrap">
                                    {record.message}
                                  </div>
                                }
                                title="메시지"
                                trigger="click"
                              >
                                <Button size="small">보기</Button>
                              </Popover>
                            ) : (
                              "없음"
                            ),
                        },
                        displayCampaign?.seeding_type === "paid" && {
                          title: "원고 제출",
                          key: "confirm_url",
                          width: 90,
                          align: "center",
                          render: (_, record) => {
                            if (!record.confirm_url) {
                              return (
                                <span className="text-[#999] text-[12px]">
                                  미제출
                                </span>
                              );
                            }
                            return (
                              <div className="flex flex-col items-center">
                                <Button
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenModal(record);
                                  }}
                                >
                                  보기
                                </Button>
                                {record.recruitment_status === "accepted" && (
                                  <div className="flex justify-center">
                                    {renderDraftReviewStatus(record)}
                                  </div>
                                )}
                              </div>
                            );
                          },
                        },
                        {
                          title: "미션",
                          dataIndex: "mission_url",
                          key: "mission_url",
                          width: 70,
                          align: "center",
                          render: (missionUrl, record) => {
                            if (!missionUrl) {
                              return (
                                <span className="text-[#999] text-[12px]">
                                  미제출
                                </span>
                              );
                            }

                            const urls = missionUrl
                              .split(",")
                              .map((url) => url.trim());

                            return (
                              <div className="flex flex-col gap-1 items-center">
                                <Popover
                                  content={
                                    <div className="max-w-[400px]">
                                      {urls.map((url, index) => (
                                        <div key={index} className="mb-2">
                                          <a
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[#1890ff] no-underline break-all block text-[12px]"
                                          >
                                            {url}
                                          </a>
                                        </div>
                                      ))}
                                    </div>
                                  }
                                  title="미션 URL"
                                  trigger="click"
                                >
                                  <Button size="small" type="link">
                                    미션 확인하기
                                  </Button>
                                </Popover>
                              </div>
                            );
                          },
                        },
                        {
                          title: "배송 정보",
                          key: "shipment",
                          width: 90,
                          align: "center",
                          render: (_, record) => {
                            // 배송이 필요한 캠페인이고, 승인된 상태일 때만 배송정보 표시
                            if (
                              displayCampaign?.requires_shipment &&
                              (record.status !== "pending" ||
                                record.recruitment_status !== "pending")
                            ) {
                              const shipment = shipmentInfo[record.id];

                              return (
                                <Popover
                                  title="배송 정보 조회"
                                  onOpenChange={(open) => {
                                    if (open)
                                      handleCheckShipmentInfo(record.id);
                                  }}
                                  content={
                                    <div className="max-w-[300px]">
                                      {shipment ? (
                                        <>
                                          <div className="mb-2">
                                            <strong>수령인:</strong>{" "}
                                            {shipment.receiver_name}
                                          </div>
                                          <div className="mb-2">
                                            <strong>연락처:</strong>{" "}
                                            {shipment.receiver_phone}
                                          </div>
                                          <div className="mb-2">
                                            <strong>국가:</strong>{" "}
                                            {shipment.receiver_country}
                                          </div>
                                          <div className="mb-2">
                                            <strong>주/도:</strong>{" "}
                                            {shipment.receiver_state_province ||
                                              "-"}
                                          </div>
                                          <div className="mb-2">
                                            <strong>도시:</strong>{" "}
                                            {shipment.receiver_city}
                                          </div>
                                          <div className="mb-2">
                                            <strong>우편번호:</strong>{" "}
                                            {shipment.receiver_postal_code}
                                          </div>
                                          <div className="mb-2">
                                            <strong>주소:</strong>{" "}
                                            {shipment.receiver_street_address}
                                          </div>
                                          {shipment.receiver_address_line && (
                                            <div className="mb-2">
                                              <strong>상세주소:</strong>{" "}
                                              {shipment.receiver_address_line}
                                            </div>
                                          )}
                                          {shipment.delivery_message && (
                                            <div className="mt-2">
                                              <strong>배송 메시지:</strong>{" "}
                                              {shipment.delivery_message}
                                            </div>
                                          )}
                                        </>
                                      ) : (
                                        <div className="text-[#999]">
                                          배송정보가 없습니다.
                                        </div>
                                      )}
                                    </div>
                                  }
                                  trigger="click"
                                >
                                  <Button size="small">조회</Button>
                                </Popover>
                              );
                            }
                            return (
                              <span className="text-[#999] text-base">-</span>
                            );
                          },
                        },
                        {
                          title: "운송장번호",
                          key: "tracking_number",
                          width: 90,
                          align: "center",
                          render: (_, record) => {
                            // 배송이 필요한 캠페인이고, 승인된 상태일 때만 운송장번호 표시
                            if (
                              displayCampaign?.requires_shipment &&
                              (record.status !== "pending" ||
                                record.recruitment_status !== "pending")
                            ) {
                              const trackingNumber = record.tracking_number;

                              if (trackingNumber) {
                                return (
                                  <span
                                    className="text-[#1890ff] font-medium cursor-pointer underline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const url = `/shipment-tracking?campaign_recruitment_id=${record.id}`;
                                      window.open(
                                        url,
                                        "배송추적",
                                        "width=450, height=900, scrollbars=yes, resizable=yes"
                                      );
                                    }}
                                  >
                                    {trackingNumber}
                                  </span>
                                );
                              } else {
                                return (
                                  <Button
                                    size="small"
                                    type="primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenTrackingModal(record);
                                    }}
                                  >
                                    등록
                                  </Button>
                                );
                              }
                            }
                            return (
                              <span className="text-[#999] text-[12px]">-</span>
                            );
                          },
                        },
                        {
                          title: "상태",
                          dataIndex: "recruitment_status",
                          key: "recruitment_status",
                          width: 120,
                          align: "center",
                          render: (status, record) => {
                            if (status === "pending") {
                              return (
                                <div className="flex gap-2 justify-center">
                                  <Button
                                    type="primary"
                                    size="small"
                                    onClick={() =>
                                      handleInfluencerAction(record, "accepted")
                                    }
                                  >
                                    수락
                                  </Button>
                                  <Popconfirm
                                    title="크리에이터 거절"
                                    description="거절하면 더 이상 수정할 수 없습니다. 계속하시겠습니까?"
                                    onConfirm={() =>
                                      handleInfluencerAction(record, "rejected")
                                    }
                                    okText="거절"
                                    cancelText="취소"
                                    placement="left"
                                    okButtonProps={{ danger: true }}
                                  >
                                    <Button danger size="small">
                                      거절
                                    </Button>
                                  </Popconfirm>
                                </div>
                              );
                            }

                            if (
                              status === "accepted" ||
                              status === "confirmed"
                            ) {
                              // mission_url이 null이 아닐 때만 완료 버튼 표시
                              const canComplete = record.mission_url;
                              const isPaidDraftReview =
                                displayCampaign?.seeding_type === "paid" &&
                                status === "accepted";

                              if (isPaidDraftReview && !canComplete) {
                                return (
                                  renderDraftReviewActions(record) || (
                                    <span className="text-[#666] text-[12px]">
                                      원고 제출 대기 중
                                    </span>
                                  )
                                );
                              }

                              return (
                                <div className="flex gap-2 justify-center">
                                  {canComplete ? (
                                    <Popconfirm
                                      title="캠페인 최종 컨펌"
                                      description="최종 컨펌 후에는 더 이상 수정할 수 없습니다. 계속하시겠습니까?"
                                      onConfirm={() =>
                                        handleInfluencerAction(
                                          record,
                                          "completed"
                                        )
                                      }
                                      okText="확인"
                                      cancelText="취소"
                                      placement="left"
                                    >
                                      <Button type="primary" size="small">
                                        최종 컨펌
                                      </Button>
                                    </Popconfirm>
                                  ) : (
                                    <span className="text-[#666] text-[12px]">
                                      {displayCampaign?.seeding_type ===
                                        "paid" && status === "accepted"
                                        ? "원고 제출 대기 중"
                                        : "미션 대기 중"}
                                    </span>
                                  )}
                                </div>
                              );
                            }
                            const statusInfo =
                              BRAND_STATUS[status] ?? BRAND_STATUS.default;
                            return (
                              <>
                                <Tag
                                  style={{
                                    backgroundColor: statusInfo.backgroundColor,
                                    color: statusInfo.color,
                                    border: "none",
                                    margin: 0,
                                  }}
                                >
                                  {statusInfo.label}
                                </Tag>
                                {status === "completed" &&
                                  displayCampaign?.seeding_type === "paid" && (
                                    <Button
                                      type="primary"
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSettlement(
                                          displayCampaign,
                                          "single",
                                          record.id
                                        );
                                      }}
                                      className="mt-2"
                                    >
                                      정산하기
                                    </Button>
                                  )}
                              </>
                            );
                          },
                        },
                      ]
                  ).filter(Boolean)}
                  rowKey="id"
                  pagination={false}
                />
                {!isInvitedTab &&
                  influencerListStatus === "completed" &&
                  displayCampaign?.id && (
                    <Router.CampaignPaymentHistorySection
                      campaignId={displayCampaign.id}
                    />
                  )}
              </>
            )}
          </div>
        ) : isDetailDrawerVisible && isEditMode ? (
          <Router.CampaignForm
            form={form}
            mode="edit"
            onFinish={handleUpdate}
            initialValues={
              editingCampaign
                ? {
                    title: editingCampaign.title,
                    description: editingCampaign.description,
                    mission: editingCampaign.mission,
                    content_format: parseContentFormatValues(
                      editingCampaign.content_format
                    ),
                    category: editingCampaign.campaign_category,
                    seeding_type: editingCampaign.seeding_type,
                    recruitment_count: editingCampaign.recruit_count,
                    payout: editingCampaign.payout,
                    currency: editingCampaign.currency,
                    requires_shipment: !!editingCampaign.requires_shipment,
                    start_date:
                      editingCampaign.start_at &&
                      dayjs(editingCampaign.start_at),
                    end_date:
                      editingCampaign.end_at && dayjs(editingCampaign.end_at),
                    thumbnail: editingCampaign.thumbnail,
                    attachment_url: editingCampaign.attachment_url,
                    product_info: editingCampaign.product_info,
                    allow_secondary_use: editingCampaign.allow_secondary_use,
                  }
                : null
            }
            campaignStatus={displayCampaign?.status}
            disabledFields={
              displayCampaign?.status === "active"
                ? [
                    "title",
                    "description",
                    "mission",
                    "content_format",
                    "category",
                    "seeding_type",
                    "currency",
                    "payout",
                    "requires_shipment",
                    "product_info",
                    "allow_secondary_use",
                  ]
                : []
            }
            hiddenFields={
              displayCampaign?.status === "active"
                ? ["thumbnail", "attachment"]
                : []
            }
            onThumbnailChange={setThumbnailFile}
            onAttachmentChange={setAttachmentFile}
          />
        ) : null}
      </Drawer>

      <Pagination
        current={currentPage}
        total={total}
        pageSize={pageSize}
        onChange={(page) => setCurrentPage(page)}
        showSizeChanger={false}
        className="justify-center items-center mt-12"
      />

      {/* 운송장번호 등록 모달 */}
      <Modal
        title="운송장번호 등록"
        open={isTrackingModalVisible}
        onCancel={() => {
          setIsTrackingModalVisible(false);
          trackingForm.resetFields();
          setSelectedCampaignRecruitmentId(null);
        }}
        footer={null}
        width={800}
        maskClosable={false}
        getContainer={() => document.body}
      >
        <Form
          form={trackingForm}
          layout="vertical"
          onFinish={handleSubmitTracking}
          requiredMark={false}
        >
          <Router.TrackingNumberForm />

          <Form.Item className="mt-6 mb-0">
            <div className="flex justify-end gap-2">
              <Button
                onClick={() => {
                  setIsTrackingModalVisible(false);
                  trackingForm.resetFields();
                  setSelectedCampaignRecruitmentId(null);
                }}
              >
                취소
              </Button>
              <Button type="primary" htmlType="submit">
                등록
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="원고 피드백"
        open={feedbackModal.open}
        onCancel={handleCloseFeedbackModal}
        onOk={() => feedbackForm.submit()}
        okText="피드백 보내기"
        cancelText="취소"
        confirmLoading={isSendingFeedback}
        destroyOnHidden
        maskClosable={false}
      >
        <Form
          form={feedbackForm}
          layout="vertical"
          onFinish={handleSendFeedback}
          requiredMark={false}
        >
          <Form.Item
            name="feedback"
            label="피드백 내용"
            rules={[
              {
                required: true,
                whitespace: true,
                message: "피드백 내용을 입력해 주세요.",
              },
            ]}
            className="mb-0"
          >
            <Input.TextArea
              rows={5}
              maxLength={1000}
              showCount
              placeholder="원고에서 수정이 필요한 내용을 입력해 주세요."
              style={{ marginBottom: 16 }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 원고 제출 보기 모달 */}
      <Router.PostingCard
        open={isModalOpen}
        record={selectedRecord}
        onClose={handleCloseModal}
      />
    </div>
  );
};
